import {Component, Input, OnInit} from '@angular/core';
import {Dad, DadSubscriberData} from '../../../models/dad';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DadService} from '../../../services/dad.service';
import {ThresholdExplanationComponent} from '../threshold-explanation/threshold-explanation.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';

@Component({
  selector: 'app-eurex-report',
  templateUrl: './eurex-report.component.html',
  styleUrls: ['../dad-form/dad-form.component.scss', './eurex-report.component.scss']
})
export class EurexReportComponent implements OnInit {

  @Input() currentDad: Dad;
  @Input() scheduleNumber: number;
  @Input() previousMonthSubscriptionData: DadSubscriberData;
  @Input() originalClientDeclarationMode = false;
  dadDeclarationForm: UntypedFormGroup;
  seePreviousMonthDeclaration = false;
  seeOriginalClientDeclaration = false;
  errors = false;
  successDeclaration = false;
  serverError = false;
  expiredError = false;

  constructor(private formBuilder: UntypedFormBuilder, private dadService: DadService,
              public dialogService: DialogService) {
  }

  ngOnInit() {
    this.seeOriginalClientDeclaration = this.originalClientDeclarationMode;
    this.createFormGroup();
  }

  createFormGroup() {
    this.dadDeclarationForm = this.formBuilder.group(
      {
        accessIds: [this.accessIds],
        authorizationCheckbox: [this.isDeclared, Validators.requiredTrue],
        confirmationCheckbox: [this.isDeclared, Validators.requiredTrue],
        restrictionCheckbox: [this.isDeclared, Validators.requiredTrue],
        thresholdExplanation: [''],
      },
    );
  }

  toggleSeePreviousMonthDeclaration() {
    this.seePreviousMonthDeclaration = !this.seePreviousMonthDeclaration;
  }

  toggleSeeOriginalClientDeclaration() {
    this.seeOriginalClientDeclaration = !this.seeOriginalClientDeclaration;
  }

  get accessIds() {
    if (!this.currentDad || (!this.currentDad.autoReport && !this.currentDad.decodedSubscriberData)) {
      return 0;
    }
    // form not submitted and no previous month declaration
    if (!(this.currentDad.decodedSubscriberData && this.currentDad.decodedSubscriberData.declarations)
      && (!this.previousMonthSubscriptionData || !this.previousMonthSubscriptionData.declarations)) {
      return 0;
    }
    // form not submitted
    if (!this.currentDad.decodedSubscriberData || !this.currentDad.decodedSubscriberData.declarations) {
      return this.previousMonthSubscriptionData.declarations[0].count;
    }
    return this.currentDad.decodedSubscriberData.declarations[0].count;
  }

  get previousUsageMonth() {
    if (!this.currentDad) {
      return;
    }
    const currentUsageMonth = new Date(this.currentDad.usageMonth);
    currentUsageMonth.setMonth(currentUsageMonth.getMonth() - 1);
    return currentUsageMonth;
  }

  onSubmit(withWarning = true) {
    if (!withWarning || confirm('Do you really want to submit this declaration?')) {
      if (!this.dadDeclarationForm.valid) {
        this.errors = true;
        this.successDeclaration = false;
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        return;
      }
      this.dadService.submitDadFormDeclaration(this.currentDad.uniqid, this.dadDeclarationForm.getRawValue()).subscribe({
          next: () => {
            this.successDeclaration = true;
            this.errors = false;
            this.serverError = false;
            if (this.dadDeclarationForm.get('thresholdExplanation').value) {
              this.currentDad.largeCountDifferenceExplanation = this.dadDeclarationForm.get('thresholdExplanation').value;
            }
            this.dadDeclarationForm.get('thresholdExplanation').setValue('');
            window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          },
          error: error => {
            if (error.status === 401) {
              this.expiredError = true;
            } else if (error.status === 406) {
              const ref = this.openThresholdExplanationPopUp();
              this.closeThresholdExplanationPopUp(ref);
              return;
            } else {
              this.serverError = true;
            }
            window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          }
        }
      );
    }
  }

  private openThresholdExplanationPopUp() {
    return this.dialogService.open(ThresholdExplanationComponent, {
      showHeader: true,
      header: 'Sudden change in declaration detected',
      width: '30%',
      style: {'max-height': '100%'},
      dismissableMask: true,
      data: {},
    });
  }

  closeThresholdExplanationPopUp(ref: DynamicDialogRef) {
    ref.onClose.subscribe(res => {
      if (!res) {
        return;
      }
      this.dadDeclarationForm.get('thresholdExplanation').setValue(res);
      this.onSubmit(false);
    });
  }

  get previousMonthDeclarationBtnLabel() {
    if (this.seePreviousMonthDeclaration) {
      return 'Hide declared count for previous month';
    }
    return 'See declared count for previous month';
  }

  get originalClientDeclarationBtnLabel() {
    if (this.seeOriginalClientDeclaration) {
      return 'Hide the client\'s original reported count';
    }
    return 'See the client\'s original reported count';
  }

  get previousMonthDeclaration() {
    if (this.previousMonthSubscriptionData && this.previousMonthSubscriptionData.declarations) {
      return this.previousMonthSubscriptionData.declarations[0].count;
    }
    return 0;
  }

  get originalClientDeclaration() {
    if (this.currentDad && this.currentDad.decodedOriginalClientDeclaration) {
      return this.currentDad.decodedOriginalClientDeclaration.declarations[0].count;
    }
    return 0;
  }

  get isDeclared() {
    return !!(this.currentDad && this.currentDad.decodedSubscriberData);
  }
}
